import { useQuery } from '@apollo/client';
import { useAuth0 } from '@auth0/auth0-react';
import { getUserModules, useLocalStorage, UserModuleIDs } from '@enroller/utils';
import { Trans, useLingui } from '@lingui/react/macro';
import { Alert, CircularProgress, Stack, Typography, useTheme } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useHeaderContext } from '../contexts/HeaderContext';
import { calcFormProgress } from '../student/calcFormProgress';
import { courseReservationDataQuery, courseReservationIdsQuery, courseReservationProgramDetailsQuery, graphQlToForm } from '../student/CourseReservation';
import { getErrorMessage } from '../utils/getErrorMessage';
import { useNotificationMessages } from '../utils/useNotificationMessages';
import { NextStepsDocuments } from './NextStepsDocuments';
import { CompletedTasksList } from './CompletedTasksList';
import { NextStepsPayment } from './NextStepsPayment';
import { NextStepsProgress } from './NextStepsProgress';
import { branding } from '../branding';

const WelcomeAlert = () => {
  const theme = useTheme();
  const [welcomeAlert, setWelcomeAlert] = useLocalStorage('welcomeAlert', true);

  if (!welcomeAlert) return <></>;

  return (
    <Alert severity="info" variant="outlined" onClose={() => setWelcomeAlert(false)}>
      <Typography variant="h5" sx={{ mb: theme.spacing(0.5) }} fontWeight={500} lineHeight={theme.spacing(1.75)}>
        <Trans>Welcome to Student Portal</Trans>
      </Typography>
      <Typography variant="body2" sx={{ fontSize: theme.spacing(1.75) }}>
        <Trans>Complete your application and begin your journey as an international student.</Trans>
      </Typography>
    </Alert>
  );
};

export const StudentDashboard = () => {
  const { user } = useAuth0();
  const { t } = useLingui();
  const { setHeaderTitle } = useHeaderContext();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [progress, setProgress] = useState<number | undefined>(undefined);
  const [depositPaid, setDepositPaid] = useState(false);
  const [applicationSubmitted, setApplicationSubmitted] = useState(false);
  const userModules = useMemo<UserModuleIDs | undefined>(() => user && getUserModules(user), [user]);
  const { refetch: fetchIds } = useQuery(courseReservationIdsQuery, { skip: true });
  const { refetch: fetchData } = useQuery(courseReservationDataQuery, { skip: true });
  const { refetch: fetchProgram } = useQuery(courseReservationProgramDetailsQuery, { skip: true });
  const { showErrorMessage } = useNotificationMessages();
  const [completedDate, setCompletedDate] = useState<string | undefined>(undefined);
  const [programPrice, setProgramPrice] = useState<number | undefined>(undefined);
  const [programCurrency, setProgramCurrency] = useState<string>('USD');
  const { withPayment, paymentProvider } = branding;

  const getCourseReservation = useCallback(
    async (dealId: string) => {
      try {
        setLoading(true);
        const { data: ids } = await fetchIds({ dealId });
        const isDepositPaid = ids?.Records?.Deals?._data[0]?.Deposit_paid?.value === 'Yes';
        const applicationCompletedDate = ids?.Records?.Deals?._data[0]?.Date_of_Contact_Details_Form_Response?.value;
        const price = ids?.Records?.Deals?._data[0]?.Price?.value;

        setApplicationSubmitted(!!applicationCompletedDate);
        setCompletedDate(applicationCompletedDate);
        setProgramPrice(price);

        if (isDepositPaid) {
          setDepositPaid(true);
          setProgress(100);
        } else if (applicationCompletedDate) {
          setProgress(100);
          setDepositPaid(false);
        } else {
          const student = ids?.Records?.Deals?._data[0]?.Contact_Name;
          const guardian1 = ids?.Records?.Deals?._data[0]?.Guardian_primary;
          const guardian2 = ids?.Records?.Deals?._data[0]?.Guardian_second;
          const { data: courseReservationData } = await fetchData({
            dealId,
            studentId: student?.id?.value || '',
            guardian1Id: guardian1?.id?.value || '',
            guardian1Exists: Boolean(guardian1?.id?.value),
            guardian2Id: guardian2?.id?.value || '',
            guardian2Exists: Boolean(guardian2?.id?.value),
          });
          setProgress(calcFormProgress(graphQlToForm(courseReservationData)));
          setDepositPaid(false);
        }

        // Fetch program currency if we have the necessary data
        if (ids?.Records?.Deals?._data[0]?.Last_Addon_Data?.value) {
          try {
            const customerData = JSON.parse(ids?.Records?.Deals?._data[0]?.Last_Addon_Data?.value);
            const { data: programData } = await fetchProgram({
              productId: customerData.current.program,
              customerCode: customerData.current.Customer_Group_code,
            });
            const currency = programData?.Records?.Products?._data[0]?.Pricing_Information__r?._data[0]?.Pricing_Currency?.value;
            if (currency) {
              setProgramCurrency(currency);
            }
          } catch {
            setProgramCurrency('USD');
          }
        }
        setError(false);
      } catch (error) {
        showErrorMessage(getErrorMessage(error));
        setError(true);
      } finally {
        setLoading(false);
      }
    },
    [fetchData, fetchIds, fetchProgram, showErrorMessage],
  );

  useEffect(() => {
    if (user?.name) {
      setHeaderTitle({ title: t`Hi, ${user.name}` });
    }
  }, [setHeaderTitle, t, user]);

  useEffect(() => {
    if (userModules?.dealId && progress === undefined && !loading && !error) {
      getCourseReservation(userModules.dealId);
    }
  }, [getCourseReservation, userModules.dealId, loading, progress, error]);

  if (loading) {
    return (
      <Stack direction="column" justifyContent="center" alignItems="center" sx={{ width: '100%', height: '100%' }} gap={2}>
        <CircularProgress />
        <Typography>
          <Trans>Loading...</Trans>
        </Typography>
      </Stack>
    );
  }

  // State 1: Deposit paid (final state) - only for brands with payment
  if (depositPaid && withPayment) {
    return (
      <Stack direction="column">
        <WelcomeAlert />
        <NextStepsDocuments depositPaid />
        <CompletedTasksList
          applicationCompletedDate={completedDate}
          depositPaidStatus="Yes"
          programPrice={programPrice}
          programCurrency={programCurrency}
          showApplicationTask={true}
          showPaymentTask={true}
          paymentProvider={paymentProvider}
        />
      </Stack>
    );
  }

  // State 2a: Application submitted, ready for payment (only for brands with payment)
  if (applicationSubmitted && progress === 100 && withPayment && !depositPaid) {
    return (
      <Stack direction="column">
        <WelcomeAlert />
        <NextStepsPayment contactId={userModules?.contactId} dealId={userModules?.dealId} paymentProvider={paymentProvider} />
        <CompletedTasksList
          applicationCompletedDate={completedDate}
          depositPaidStatus="No"
          programPrice={programPrice}
          programCurrency={programCurrency}
          showApplicationTask={true}
          showPaymentTask={true}
          paymentProvider={paymentProvider}
        />
      </Stack>
    );
  }

  // State 2b: Application submitted, no payment required (final state for non-payment brands)
  if (applicationSubmitted && progress === 100 && !withPayment) {
    return (
      <Stack direction="column">
        <WelcomeAlert />
        <NextStepsDocuments depositPaid={false} />
        <CompletedTasksList
          applicationCompletedDate={completedDate}
          depositPaidStatus="No"
          programPrice={programPrice}
          programCurrency={programCurrency}
          showApplicationTask={true}
          showPaymentTask={false}
        />
      </Stack>
    );
  }

  // State 3: Form complete (100%) but not submitted - show submit step
  if (progress === 100 && !applicationSubmitted) {
    return (
      <Stack direction="column">
        <WelcomeAlert />
        <NextStepsDocuments depositPaid={false} />
        <CompletedTasksList
          applicationCompletedDate={completedDate}
          depositPaidStatus="No"
          programPrice={programPrice}
          programCurrency={programCurrency}
          showApplicationTask={false}
          showPaymentTask={false}
        />
      </Stack>
    );
  }

  // State 4: Form in progress (default)
  return (
    <Stack direction="column" spacing={3}>
      <WelcomeAlert />
      <NextStepsProgress progress={progress} />
    </Stack>
  );
};
